import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {HeightToNormalSockets} from "#material/nodes/converter/height-to-normal/definition"
import {heightToNormalDefinition} from "#material/nodes/converter/height-to-normal/definition"
import type {CyclesReturns} from "#material/sockets/cycles"
import {threeRGBColor, threeValue} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import type {Three as THREE} from "#material/three/exports"
import {ThreeTsl as TSL} from "#material/three/exports"
import type {ThreeReturns} from "#material/sockets/three"
import {threeEncodeNormal, threeHeightToNormal} from "#material/nodes/converter/height-to-normal/constants"
import {isTextureNodeLike} from "@cm/three-texture-streaming"

@registerMaterialGraphNode
export class HeightToNormal extends MaterialNode<HeightToNormalSockets> {
    public override get definition() {
        return heightToNormalDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<HeightToNormalSockets>> => {
        const {displacementMax, displacementMin, displacementNormalStrength, displacementNormalSmoothness} = this.parameters.settings

        const scaleFromDisplacement = displacementMax !== undefined && displacementMin !== undefined ? displacementMax! - displacementMin! : 1
        const smoothness = displacementNormalSmoothness ?? 1
        const scale = threeValue((displacementNormalStrength ?? scaleFromDisplacement) * smoothness)

        const result = await evaluator.get(this.parameters.inputs.color)
        if (result === undefined || result === null) {
            return {color: threeRGBColor({r: 0.5, g: 0.5, b: 1.0})}
        }

        // the gradient comes back per texel, so smoothness divides it to keep the slope independent of resolution
        const colorNode = result as THREE.Node
        if (isTextureNodeLike(colorNode)) {
            const uv = (colorNode.uvNode ?? TSL.uv(0)).xy as THREE.Node<"vec2">
            const {dFdU, dFdV} = colorNode.sampleGradient(uv)
            const gradUV = TSL.vec2(dFdU.r, dFdV.r).div(TSL.float(smoothness))
            return {color: TSL.color(threeEncodeNormal(gradUV, scale, uv))}
        }

        // Procedural / virtual path: screen-space derivatives corrected via UV Jacobian.
        return {color: TSL.color(threeHeightToNormal(result.r, scale))}
    }

    override toCycles = async (_evaluator: CyclesEvaluator): Promise<CyclesReturns<HeightToNormalSockets>> => {
        throw new Error("Cycles support for HeightToNormal is not implemented.")
    }
}
